import { useShopAvailability } from "../../components/shopAvailability";
import { formatMoney } from "../../functions/money";
import { useCartDrawer } from "../../components/cartDrawer/context";
import { ProductImage } from "../../components/productImage";
import { useState } from "react";
import { useProductPrice } from "../../hooks/useProductPrice";
import { useSelector } from "react-redux";
import { findCartLineIndex } from "../../reducers/cartLineIdentity";
import { Textarea } from "@delibrandev/utils";
import { LuMinus, LuPlus } from "react-icons/lu";
import { ProductAllergens } from "./ProductAllergens";
import { CartActions } from "../../components/cartActions";

import { Combinations } from "./combinations";
import { Attributes } from "./Attributes";
import { visibleItemGroups } from "./itemGroups";

export const ProductContent = ({ productData, modal = false, onAdded }) => {
  const { openCart } = useCartDrawer();
  const availability = useShopAvailability();
  const [itemIds, setItemIds] = useState([]);
  const [variantId, setVariantId] = useState("");
  const selectedVariant = (productData?.variants || []).find(variant => variant.variantId === variantId);
  const usableImages = images => Array.isArray(images)
    ? images.filter(image => typeof image === "string" && image.trim()) : [];
  const variantImages = usableImages(selectedVariant?.images);
  const detailImages = variantImages.length ? variantImages : usableImages(productData?.images);
  const [note, setNote] = useState("");
  const [draftQuantity, setDraftQuantity] = useState(1);
  const afterCartAction = change => { if (change > 0) { onAdded?.(); openCart({ productId: productData.productId, variantId, itemIds, note }); } };
  const cart = useSelector((state) => state.aplicationConfig.userConfig.cart);
  const quantity = (cart?.ShoppingCartProducts || []).reduce((count, line) => {
    const matches = findCartLineIndex([line], {
      productId: productData?.productId, variantId, itemIds, note,
    }) === 0;
    return count + (matches ? line.quantity : 0);
  }, 0);
  const [requirements, updateRequirements] = useState("");
  const handleOption = (item) => {
    setItemIds((prevItemIds) => {
      if (prevItemIds.includes(item.itemId)) {
        // Si ya existe, lo eliminamos
        return prevItemIds.filter((id) => id !== item.itemId);
      } else {
        // Si no existe, lo añadimos
        return [...prevItemIds, item.itemId];
      }
    });
  };

  const {price, lineTotal, busy:pricing, error:priceError, retry:retryPrice} = useProductPrice(productData, variantId, itemIds, modal ? draftQuantity : 1);
  const renderPrice = () => priceError ? (
    <div><span role="alert">No se pudo calcular el precio. Vuelve a intentarlo.</span><button type="button" onClick={retryPrice}>Reintentar precio</button></div>
  ) : pricing ? <span role="status">Calculando precio…</span> : <span>{modal ? formatMoney(price) : `${price.toFixed(2)}€`} <small>IVA incluido</small></span>;


  return (
    <div className={`productList eats-product-detail ${modal ? "eats-modal-product" : ""}`}>
      <div className="productContainer" style={{ marginTop: "15px" }}>
        <div className="form w-100">
          <ProductImage src={detailImages[0]} name={productData.name} className="eats-detail-image" />

          {detailImages.length > 1 && (
            <div className="eats-detail-gallery">
              {detailImages.map((image, id) => <ProductImage key={image || id} src={image} name={`${productData.name}, imagen ${id + 1}`} />)}
            </div>
          )}


        </div>

        <div className="w-100">
          <div className="stickyContent">
            <div className="form p-2" style={{ gap: "1px" }}>
              <div className="details-name" style={{ marginBottom: "-10px" }}>
                <h3 id={modal ? "product-modal-title" : undefined}>{productData.name}</h3>
              </div>
              {productData.description && (
                <div className="details-description">
                  <div>{productData.description}</div>
                </div>
              )}
            </div>

            {modal && <div className="eats-modal-product-price">{renderPrice()}</div>}
            <ProductAllergens allergens={productData?.allergens?.allergens || productData?.allergens || {}} />
            <div className="desktopRender" style={{ paddingTop: "0px", paddingBottom: "0px", margin: "0 8px" }}>
              <div className="details-resume">
                <div className="details-resume-total">{renderPrice()}</div>
                <div className="details-resume-actions">
                  {requirements === "" ? (
                    <CartActions
                      data={{ ...productData, quantity: quantity }}
                      itemIds={itemIds}
                      variantId={variantId}
                      note={note}
                      callbackAction={afterCartAction}
                      showRemove={quantity > 0}
                      disabled={pricing || priceError}
                      showCounter={true}
                    />
                  ) : (
                    <div style={{ fontSize: "0.8rem", color: "var(--color-pending)" }}>{requirements}</div>
                  )}
                </div>
              </div>
            </div>
            <hr style={{ margin: "15px 0 0 0" }} />
          </div>

          <div className="form p-2">
            <div className="details-block eats-combinations-block">
              <Combinations productData={productData} variantId={variantId} setVariantId={setVariantId} />
            </div>

            {visibleItemGroups(productData.itemGroups).map((group, index) => (
              <div key={index} className="details-block">
                <Attributes
                  group={group}
                  tax={productData.tax}
                  index={index}
                  handleOption={handleOption}
                  itemIds={itemIds}
                  updateRequirements={updateRequirements}
                />
              </div>
            ))}

            <div className="details-block eats-kitchen-notes">
              <div className="details-name flex-gap">
                <label htmlFor="notes">Notas para cocina</label>
                <span className="eats-optional">Opcional</span>
              </div>
              <Textarea id="notes" placeholder="Ej. sin cebolla, poco hecho…" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {modal ? <footer className="eats-product-modal-footer">
        {!availability.canOrder && <p role="status">{availability.message || "Comprobando horario de pedidos…"}</p>}
        {requirements && <p role="status">{requirements}</p>}
        {!requirements && !variantId && productData.variantsOrdered?.length > 0 && <p role="status">Selecciona una combinación para continuar</p>}
        <div className="eats-draft-quantity" role="group" aria-label="Cantidad del producto">
          <button type="button" aria-label="Menos unidades" disabled={draftQuantity <= 1} onClick={() => setDraftQuantity(value => Math.max(1, value - 1))}><LuMinus aria-hidden="true" /></button>
          <output aria-live="polite" aria-label="Cantidad">{draftQuantity}</output>
          <button type="button" aria-label="Más unidades" disabled={draftQuantity >= Number.MAX_SAFE_INTEGER} onClick={() => setDraftQuantity(value => value + 1)}><LuPlus aria-hidden="true" /></button>
        </div>
        {priceError && <p role="alert">No se pudo calcular el importe. <button type="button" onClick={retryPrice}>Reintentar precio</button></p>}
        <CartActions data={{ ...productData, quantity }} itemIds={itemIds} variantId={variantId} note={note}
          callbackAction={afterCartAction} showRemove={false} showCounter={false}
          addQuantity={draftQuantity} singleSubmit
          addLabel={pricing ? "Calculando…" : `Añadir al pedido${!priceError && Number.isFinite(lineTotal ?? price) ? ` · ${formatMoney(lineTotal ?? price)}` : ""}`} disabled={pricing || priceError || !!requirements || (!variantId && productData.variantsOrdered?.length > 0)} />
      </footer> : (
      <div
        className="stickyContentProductBottom mobileRender"
        style={{ paddingTop: "0px", paddingBottom: "0px", margin: "0 8px" }}
      >
        <div className="details-resume" style={{ backgroundColor: "white", padding: "15px 0" }}>
          <div className="details-resume-total">{renderPrice()}</div>
          <div className="details-resume-actions">
            {requirements === "" ? (
              <CartActions
                data={{ ...productData, quantity: quantity }}
                itemIds={itemIds}
                variantId={variantId}
                note={note}
                      callbackAction={afterCartAction}
                showRemove={quantity > 0}
                      disabled={pricing || priceError}
                showCounter={true}
              />
            ) : (
              <div style={{ fontSize: "0.8rem", color: "var(--color-pending)" }}>{requirements}</div>
            )}
          </div>
        </div>
      </div>)}
    </div>
  );
};
